import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ShareCard } from "../components/ShareCard";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Modal, Avatar } from "../components/Layout";
import { fmtDate } from "../lib/dates";
import { Plus, Trash2, ImagePlus, Lock } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export default function Moments() {
  const { user } = useAuth();
  const [moments, setMoments] = useState(null);
  const [pods, setPods] = useState([]);
  const [community, setCommunity] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", date: "", pod_id: "", community_id: "", image_path: "" });
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const [selected, setSelected] = useState(null);
  const [editMoment, setEditMoment] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", date: "" });
  const [shareCard, setShareCard] = useState(null);

  const load = () => {
    api.get("/moments").then((r) => setMoments(r.data));
    api.get("/pods/mine").then((r) => setPods(r.data)).catch(() => {});
    api.get("/community").then((r) => setCommunity(r.data.community ? { id: r.data.community.chapter_id, name: r.data.community.name } : null)).catch(() => {});
  };
  useEffect(load, []);

  const openEdit = (m) => {
    setSelected(null);
    setEditMoment(m);
    setEditForm({ title: m.title, description: m.description || "", date: m.date || "" });
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) return toast.error("Give this moment a title");
    try {
      await api.put(`/moments/${editMoment.moment_id}`, editForm);
      toast.success("Moment updated");
      setEditMoment(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const sendToChat = (m) => {
    setSelected(null);
    setShareCard({
      image: m.image_path ? `${api.defaults.baseURL}/files/${m.image_path}` : null,
      eyebrow: m.pod_name || m.community_name || "A Bouquet moment",
      title: m.title,
      body: m.description || "",
      footer: `${m.author?.name || "Me"} · ${fmtDate(m.date)} · Bouquet`,
    });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/image", fd);
      setForm((f) => ({ ...f, image_path: res.data.path }));
      setPreview(`${api.defaults.baseURL}/files/${res.data.path}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const create = async () => {
    if (!form.title.trim()) return toast.error("Give this moment a title");
    try {
      await api.post("/moments", { ...form, pod_id: form.pod_id || null, community_id: form.community_id || null, image_path: form.image_path || null, date: form.date || new Date().toISOString().slice(0, 10) });
      toast.success("Moment preserved");
      setShowCreate(false);
      setForm({ title: "", description: "", date: "", pod_id: "", community_id: "", image_path: "" });
      setPreview("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save moment");
    }
  };

  const remove = async (m) => {
    await api.delete(`/moments/${m.moment_id}`);
    toast.success("Moment removed");
    load();
  };

  if (!moments) return <div className="animate-pulse text-muted-foreground text-sm">Gathering moments…</div>;

  return (
    <div data-testid="moments-page" className="space-y-8">
      <PageHeader eyebrow="Moments" title="Moments Worth Keeping"
        description="Accomplishments, milestones, celebrations, traditions — preserved intentionally, not performed.">
        <button data-testid="create-moment-button" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
          <Plus className="h-4 w-4" /> Preserve a moment
        </button>
      </PageHeader>

      {moments.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-10 text-center space-y-3">
          <p className="font-display text-xl font-medium">No moments yet</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">The first one can be small. A dinner that mattered. A step that finally happened.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {moments.map((m) => (
          <div key={m.moment_id} onClick={() => setSelected(m)}
            className="bg-card border border-border rounded-xl p-6 space-y-3 cursor-pointer hover:border-primary transition-colors" data-testid={`moment-card-${m.moment_id}`}>
            {m.image_path && (
              <img src={`${api.defaults.baseURL}/files/${m.image_path}`} alt={m.title} data-testid={`moment-image-${m.moment_id}`}
                className="w-full h-60 object-cover rounded-lg" />
            )}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-display text-lg font-medium">{m.title}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(m.date)}</p>
              </div>
              {m.is_mine && (
                <button data-testid={`moment-delete-${m.moment_id}`} onClick={(e) => { e.stopPropagation(); remove(m); }}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive transition-colors shrink-0">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {m.description && <p className="text-sm text-muted-foreground leading-relaxed">{m.description}</p>}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar user={m.author} size="h-6 w-6" />
                <span className="text-xs text-muted-foreground">{m.author?.name}</span>
              </div>
              {m.pod_name ? (
                <span className="px-2.5 py-1 rounded-full bg-secondary text-[11px] font-semibold">{m.pod_name}</span>
              ) : m.community_name ? (
                <span className="px-2.5 py-1 rounded-full bg-sage-pale text-[11px] font-semibold" data-testid={`moment-community-badge-${m.moment_id}`}>{m.community_name}</span>
              ) : (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Personal</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} testid="create-moment-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Preserve a moment</h3>
            <input data-testid="moment-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What happened?" className={inputCls} />
            <textarea data-testid="moment-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} placeholder="Why it mattered (optional)…" className={inputCls} />
            <input data-testid="moment-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            <div>
              <p className="text-sm font-semibold mb-1.5">Shared with <span className="font-normal text-muted-foreground">(personal by default)</span></p>
              <select data-testid="moment-pod-select" value={form.community_id ? "community" : form.pod_id}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, pod_id: v === "community" ? "" : v, community_id: v === "community" ? community.id : "" });
                }} className={inputCls}>
                <option value="">Just me (personal)</option>
                {community && <option value="community">{community.name} (my community)</option>}
                {pods.map((p) => <option key={p.pod_id} value={p.pod_id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <input data-testid="moment-image-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" id="moment-image-file" onChange={handleFile} />
              <label htmlFor="moment-image-file" data-testid="moment-image-upload-button"
                className="flex items-center justify-center gap-2 w-full px-3.5 py-2.5 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:bg-secondary cursor-pointer transition-colors">
                <ImagePlus className="h-4 w-4" /> {uploading ? "Uploading…" : preview ? "Change photo" : "Add a photo (optional)"}
              </label>
              {preview && <img src={preview} alt="Moment preview" data-testid="moment-image-preview" className="mt-2 w-full h-32 object-cover rounded-lg" />}
            </div>
            <button data-testid="moment-submit-button" onClick={create}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Keep this moment</button>
          </div>
        </Modal>
      )}

      <ShareCard card={shareCard} onDone={() => setShareCard(null)} />

      {selected && (
        <Modal onClose={() => setSelected(null)} testid="moment-detail-modal">
          <div className="space-y-4">
            {selected.image_path && (
              <img src={`${api.defaults.baseURL}/files/${selected.image_path}`} alt={selected.title}
                className="w-full max-h-96 object-cover rounded-lg" data-testid="moment-detail-image" />
            )}
            <div>
              <h3 className="font-display text-2xl font-medium" data-testid="moment-detail-title">{selected.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {selected.author?.name} · {fmtDate(selected.date)}{selected.pod_name ? ` · ${selected.pod_name}` : selected.community_name ? ` · ${selected.community_name}` : " · Personal"}
              </p>
            </div>
            {selected.description && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">The reflection</p>
                <p className="text-sm leading-relaxed" data-testid="moment-detail-reflection">{selected.description}</p>
              </div>
            )}
            {selected.is_mine && (
              <div className="flex gap-2 pt-1">
                <button data-testid="moment-edit-button" onClick={() => openEdit(selected)}
                  className="flex-1 border border-border py-2.5 rounded-full text-sm font-semibold hover:bg-secondary transition-colors">Edit</button>
                <button data-testid="moment-send-chat-button" onClick={() => sendToChat(selected)}
                  className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">Send to chat</button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {editMoment && (
        <Modal onClose={() => setEditMoment(null)} testid="moment-edit-modal">
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium">Edit moment</h3>
            <input data-testid="moment-edit-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Title" className={inputCls} />
            <textarea data-testid="moment-edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} placeholder="The story behind it…" className={inputCls} />
            <input data-testid="moment-edit-date" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className={inputCls} />
            <button data-testid="moment-edit-save" onClick={saveEdit}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Save changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
